import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { appointmentKeys } from '@/hooks/use-appointments';
import { useAuthHydration } from '@/hooks/use-auth-hydration';
import { providerKeys } from '@/hooks/use-provider';
import { serviceKeys } from '@/hooks/use-services';
import api from '@/services/api';
import {
  AssistantStatus,
  AssistantThread,
  AssistantTurn,
} from '@/types/assistant';
import { OnboardingDraft } from '@/types/onboarding-interview';
import { interviewKeys } from './use-onboarding-interview';

const BASE = '/assistant';

export const assistantKeys = {
  all: ['assistant'] as const,
  status: () => [...assistantKeys.all, 'status'] as const,
  thread: () => [...assistantKeys.all, 'thread'] as const,
};

/** Sem chave de IA no servidor, a aba do assistente nem é oferecida. */
export function useAssistantStatus() {
  const isHydrated = useAuthHydration();
  return useQuery({
    queryKey: assistantKeys.status(),
    queryFn: async () => {
      const res = await api.get<AssistantStatus>(`${BASE}/status`);
      return res.data;
    },
    enabled: isHydrated,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/**
 * Aceite do envio de dados à IA de terceiros (App Store 5.1.2(i)). Vale para
 * o assistente e para a entrevista do cadastro: os dois status são refeitos.
 */
export function useAiConsent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (accept: boolean) => {
      if (accept) await api.post(`${BASE}/consent`);
      else await api.delete(`${BASE}/consent`);
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: assistantKeys.status() }),
        queryClient.invalidateQueries({ queryKey: interviewKeys.status() }),
      ]),
  });
}

/** O fio: é com ele que a aba é remontada. Abrir não gasta chamada ao modelo. */
export function useAssistantThread(enabled = true) {
  const isHydrated = useAuthHydration();
  return useQuery({
    queryKey: assistantKeys.thread(),
    queryFn: async () => {
      const res = await api.get<AssistantThread>(`${BASE}/thread`);
      return res.data;
    },
    enabled: isHydrated && enabled,
  });
}

/**
 * Um turno. **Nunca escreve nada**: quando o assistente prepara uma mudança, a
 * resposta traz `pendingActionId` e o cartão de confirmação, e só.
 */
export function useAssistantMessage() {
  return useMutation({
    mutationFn: async (text: string) => {
      const res = await api.post<AssistantTurn>(`${BASE}/message`, { text });
      return res.data;
    },
  });
}

/**
 * "Confirmar" no cartão: a única escrita do assistente.
 *
 * Depois dela o prestador tem serviço, grade ou regra de agenda diferente do
 * que estava em cache — por isso a invalidação larga. Errar para menos aqui
 * deixaria a tela de serviços mostrando o mundo de antes da confirmação.
 */
export function useConfirmAssistantAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (actionId: string) => {
      const res = await api.post<AssistantTurn>(
        `${BASE}/actions/${actionId}/confirm`,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceKeys.all });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
      queryClient.invalidateQueries({ queryKey: providerKeys.all });
      queryClient.invalidateQueries({ queryKey: assistantKeys.thread() });
    },
  });
}

/** "Cancelar" no cartão: a proposta morre sem deixar nada para trás. */
export function useCancelAssistantAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (actionId: string) => {
      const res = await api.post<AssistantTurn>(
        `${BASE}/actions/${actionId}/cancel`,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assistantKeys.thread() });
    },
  });
}

/** "Limpar conversa". Não desfaz nada que já foi confirmado. */
export function useClearAssistantThread() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.delete<AssistantThread>(`${BASE}/thread`);
      return res.data;
    },
    onSuccess: thread => {
      queryClient.setQueryData(assistantKeys.thread(), thread);
    },
  });
}

/**
 * "Refazer configuração do meu negócio": zera o rascunho e reabre a entrevista.
 *
 * Não desfaz nada do que já foi aplicado — o apply é aditivo e idempotente, e
 * apagar serviço que o prestador talvez já use seria um estrago maior do que o
 * problema que ele veio resolver.
 */
export function useRestartInterview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.post<OnboardingDraft>(
        '/activity-packs/interview/restart',
      );
      return res.data;
    },
    onSuccess: draft => {
      queryClient.setQueryData(interviewKeys.draft(), draft);
      queryClient.invalidateQueries({ queryKey: interviewKeys.all });
    },
  });
}
